import { Grid, Pagination } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../reduxService/hooks';
import { IProjectBaseResponse } from '../../api/IApiResponse';
import { EProjectType } from '../../enum/project';
import * as BeMaccaferriStoreHelper from '../../api/store/BeMaccaferriStoreHelper';
import * as ProjectApiService from '../../services/ProjectApiService';
import { selectUser } from '../../store/user/userSlice';
import { IRecentProjectsProps } from './IRecentProjects';
import ProjectItem from './ProjectItem/ProjectItem';
import './RecentProjects.scss';
import Button from '../../../components/Button/Button';
import Dialog from '../../../components/Dialog/Dialog';
import { cleanProjectInfo } from '../../store/projectInfo/projectInfoSlice';
import { cleanProject, setProject } from '../../Dynamic/store/project/projectslice';
import { cleanResults } from '../../Dynamic/store/project/resultsSlice';
const MIN_PROJECT_PAGE_SIZE = 10;

const RecentProjects = (props: IRecentProjectsProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [projects, setProjects] = useState<IProjectBaseResponse[]>([]);
    const user = useAppSelector(selectUser);

    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [DeleteConfirmDialog, setDeleteConfirmDialog] = useState<boolean>(false);
    const [DeleteResultsDialog, setDeleteResultsDialog] = useState<string>('');
    const [ProjectToDel, setProjectToDel] = useState<IProjectBaseResponse | null>(null);

    const fetchProjects = async (page: number) => {
        let currentPage = page;
        let projectByCompany = await BeMaccaferriStoreHelper.getProjectsAsync(currentPage, MIN_PROJECT_PAGE_SIZE);

        while (projectByCompany.data.items.length == 0 && currentPage > 1) {
            currentPage--;
            projectByCompany = await BeMaccaferriStoreHelper.getProjectsAsync(currentPage, MIN_PROJECT_PAGE_SIZE);
        }

        // Reason: Sort projects by lastModified date in descending order (most recent first)
        const sortedProjects = [...projectByCompany.data.items].sort((a, b) => {
            const dateA = new Date(a.lastModified).getTime();
            const dateB = new Date(b.lastModified).getTime();
            return dateB - dateA;
        });

        setPage(currentPage);
        setProjects(sortedProjects);
        setTotalPages(projectByCompany.data.totalPages);
        setTotalItems(projectByCompany.data.totalCount);
        console.log('RecentProjects - projects in recent:', sortedProjects);

        // Enrich projects with types (parallel API calls)
        enrichProjectsWithTypes(sortedProjects, currentPage);
    };

    const enrichProjectsWithTypes = async (projectsList: IProjectBaseResponse[], currentPage: number) => {
        // Get only projects visible on current page
        const projectsToEnrich = projectsList.slice((currentPage - 1) * MIN_PROJECT_PAGE_SIZE, currentPage * MIN_PROJECT_PAGE_SIZE);

        // Parallel API calls for each project
        const typePromises = projectsToEnrich.map(async (project) => {
            if (!project.guid) return { guid: null, type: null };
            try {
                const response = await ProjectApiService.loadProjectByGuid(String(project.guid));
                const type = response.data?.projectInfo?.data?.type ?? null;
                return { guid: project.guid, type };
            } catch {
                return { guid: project.guid, type: null };
            }
        });

        const typesResult = await Promise.all(typePromises);

        // Merge types into projects
        setProjects((prevProjects) =>
            prevProjects.map((p) => {
                const typeInfo = typesResult.find((t) => t.guid === p.guid);
                if (typeInfo?.type) {
                    return {
                        ...p,
                        projectInfo: {
                            ...p.projectInfo,
                            data: { ...(p.projectInfo?.data || {}), type: typeInfo.type },
                        },
                    } as IProjectBaseResponse;
                }
                return p;
            })
        );
    };

    const dispatch = useAppDispatch();
    const newProject = () => {
        dispatch(cleanProjectInfo());
        navigate('/project-info');
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!!user.data?.email) {
                await fetchProjects(1);
            }
        };

        fetchData();
    }, [user.data]);

    const handleClick = async (e: React.MouseEvent, project: IProjectBaseResponse) => {
        if (!!props.onClose) {
            props.onClose();
        }
        
        // Clean Dynamic project and results state before navigating
        dispatch(cleanProject());
        dispatch(cleanResults());
        
        console.log(`RecentProjects - Project clicked: ${project.projectName}, guid: ${project.guid}`);

        if (!project.guid) {
            console.error('RecentProjects - Project guid is missing');
            return;
        }

        const guidString = String(project.guid);

        // Reason: Backend list API returns projectInfo: null, so we need to load full project to determine type
        try {
            // Load full project data to get the actual type
            const loadResult = await ProjectApiService.loadProjectByGuid(guidString);
            
            if (!loadResult.success || !loadResult.data) {
                console.error(`RecentProjects - Failed to load project ${guidString}:`, loadResult.error);
                // Fallback: navigate to designCondition if we can't load the project
                navigate(`designCondition/${guidString}`);
                return;
            }

            const fullProject = ProjectApiService.transformProjectFromBackend(loadResult.data);
            const projectType = fullProject.type;

            console.log(`RecentProjects - Project type determined: ${projectType}`);

            // 🔧 FIX: Dispatch loaded project to Redux (Single Source of Truth pattern)
            // Prevents duplicate API call in useProjectFormSetupModular when navigating
            dispatch(setProject(fullProject));

            // Navigate based on actual project type
            if (projectType === EProjectType.geogrid) {
                console.log(`RecentProjects - Navigating to geogrid route for project: ${guidString}`);
                navigate(`geogrid/${guidString}`);
            } else {
                console.log(`RecentProjects - Navigating to designCondition route for project: ${guidString}`);
                navigate(`designCondition/${guidString}`);
            }
        } catch (error) {
            console.error('RecentProjects - Error loading project on click:', error);
            // Fallback: navigate to designCondition if there's an error
            navigate(`designCondition/${guidString}`);
        }
    };

    const handleDelete = async (e: React.MouseEvent, project: IProjectBaseResponse) => {
        setProjectToDel(project);
        setDeleteConfirmDialog(true);
        /*Alert.warning(t('recent_projects_alert_warning_title'), t('recent_projects_alert_warning_body') as string).then(async result => {
            if (result.isConfirmed) {
                let isDeleted = await BeMaccaferriStoreHelper.deleteProjectByIdAsync(project.projectId!);
                if (isDeleted) {
                    let newProjects = projects!.filter(proj => proj.projectId !== project.projectId);
                    setProjects(newProjects);
                    Alert.success(t('recent_projects_alert_success_title'), t('recent_projects_alert_success_body') as string);
                }
            }
        });*/
    };

    const confirmDelete = async () => {
        setDeleteConfirmDialog(false);
        if (!ProjectToDel?.guid) return;
        try {
            // Use new API: DELETE /api/Project/projects/{guid}
            // Convert number guid to string for API compatibility
            const guidString = String(ProjectToDel.guid);
            console.log(`RecentProjects - Deleting project: ${guidString}`);
            const deleteResult = await ProjectApiService.deleteProjectFromBackend(guidString);

            if (deleteResult.success) {
                console.log(`RecentProjects - ✅ Project deleted successfully: ${guidString}`);
                fetchProjects(page); // Refresh project list
                setDeleteResultsDialog(t('functions.projects.deleteText') as string);
            } else {
                console.error(`RecentProjects - ❌ Delete failed:`, deleteResult.error);
                setDeleteResultsDialog(t('functions.projects.deleteError') as string);
            }
        } catch (ex) {
            console.error('RecentProjects - Error deleting project:', ex);
            setDeleteResultsDialog(t('functions.projects.deleteError') as string);
        }
    };

    const handlePageChange = (page: number) => {
        fetchProjects(page);
        setPage(page);
    };

    return (
        <>
            <Dialog
                open={DeleteConfirmDialog}
                title={`${t('warnings.generic.title')}`}
                onClose={() => setDeleteConfirmDialog(false)}
                actions={[
                    { text: `${t('warnings.generic.cancel')}`, callback: () => setDeleteConfirmDialog(false), color: 'secondary' },
                    { text: `${t('warnings.generic.confirm')}`, callback: () => confirmDelete(), color: 'primary' },
                ]}>
                <>
                    <div>{t('warnings.projects.deleteConfirm')}</div>
                </>
            </Dialog>
            <Dialog open={!!DeleteResultsDialog && DeleteResultsDialog.length > 0} title={`${t('functions.projects.deleteTitle')}`} onClose={() => setDeleteResultsDialog('')}>
                <>
                    <div>{DeleteResultsDialog}</div>
                </>
            </Dialog>
            <Grid container style={{ paddingTop: '80px', alignItems: 'center', marginBottom: '40px' }} spacing={2}>
                <Grid item xs={8} style={{ paddingTop: '0px' }}>
                    <label className="tableTitle">{t('home_recent_projects_table_title')}</label> <span className="counter">{`[${totalItems ?? 0}]`}</span>
                </Grid>
                <Grid item xs={4}>
                    <Button className="d-flex justify-content-end" classNameBtn="newProjectButton" onClick={newProject}>
                        {t('home_new_project_button')}
                        <i className="fa-solid fa-plus-circle" />
                    </Button>
                </Grid>
            </Grid>

            {projects.length > 0 && (
                <>
                    <div className="d-flex p-1 me-3 headerTabella">
                        <label className="w-30 me-5 tableHeader">{t('home_recent_projects_table_name')}</label>
                        <label className="w-30 me-5 tableHeader">{t('home_recent_projects_table_last_update')}</label>
                        <label className="w-30 tableHeader">{t('home_recent_projects_table_type')}</label>
                    </div>
                    <hr />
                    {projects
                        .slice((page - 1) * MIN_PROJECT_PAGE_SIZE, page * MIN_PROJECT_PAGE_SIZE)
                        .map((project: IProjectBaseResponse, index: number) => (
                            <ProjectItem project={project} onClick={e => handleClick(e, project)} onDelete={e => handleDelete(e, project)} key={index} />
                        ))}
                    {page <= totalPages && totalPages !== 1 && (
                        <Pagination count={totalPages} page={page} onChange={(e, value) => handlePageChange(value)} className="d-flex justify-content-end mt-3" />
                    )}
                </>
            )}
        </>
    );
};

export default RecentProjects;

export {};
